import { User } from 'oidc-client';
import { Dispatch, SetStateAction } from 'react';
import { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';
import {
  setCpmUserType,
  setIsCpmEvpFieldDisabled,
  setIsCpmRegionFieldDisabled,
  setIsGlobalUser,
  setIsUserRegional,
  setIsUserGlobalOrSetAsia,
  setLoggedInUserEmail,
  setLoggedInUserName,
  setLoggedInUserRegion,
  setLoggedInUserRoles,
  setShowEvpData,
  setShowScenarioModellingPage,
  setUserEvp,
  setUserRegionFlags,
  setIsUserGlobalButNcfGlobal,
  setShowDataRefreshPage
} from '../../CarbonDashboard/redux/dashboard/dashboardSlice';
import { IUserInfo } from '../../CarbonDashboard/Types/types';
import { authorizedUatUsers, THREE } from '../../Tanso/api/constants';
import jwt_decode from 'jwt-decode';
import { Action, IuserRegionFlags } from '../../Tanso/Types/types';
import { IShowEvpData } from '../../CarbonDashboard/redux/dashboard/dashboard.types';
import { EVP_SE, EVP_SET, permissibleAreas } from '../../CarbonDashboard/api/constants';
import { AppDispatch } from '../../CarbonDashboard/redux/store';
import { getRegionVal } from '../../CarbonDashboard/utils';
import { devTeam, userRoleForDevloper } from '../../CarbonDashboard/constants';

export const getUserAction = (role: string) => {
  return role.split('_')[1];
};

export const getUserRoleAndRegion = (roles: string) => {
  const [, role, region] = roles.split('_');

  return {
    role: role.toUpperCase(),
    region: region.toUpperCase(),
  };
};

export interface IUserDetails {
  region: string;
  userId: string;
  email: string;
  roleType: string;
}

export const setUpRoleBasedAccess = (
  roles: string[],
  userId: string,
  email: string,
  dispatch: Dispatch<Action>,
  cpmDispatch: ReduxDispatch,
  isProdEnv: boolean,
) => {
  const { role: roleType, region } = getUserRoleAndRegion(roles[0]);

  // Initialize user type flags
  const userRegionFlags: IuserRegionFlags = {
    isAsiaUser: false,
    isAmericasUser: false,
    isAustraliaUser: false,
    isEuropeUser: false,
  };

  // Iterate over permissible areas to set user type flags
  Object.keys(permissibleAreas).forEach((key) => {
    if (region.includes(permissibleAreas[key as keyof typeof permissibleAreas])) {
      userRegionFlags[key as keyof typeof userRegionFlags] = true;
    }
  });

  // Example usage of userRegionFlags
  const { isAustraliaUser, isEuropeUser, isAsiaUser, isAmericasUser } = userRegionFlags;

  const userRegionVars = {
    isAustraliaUser,
    isEuropeUser,
    isAsiaUser,
    isAmericasUser,
  };
  const userDetails: IUserDetails = { region, userId, email, roleType };
  setLoggedInUserStates(dispatch, userRegionVars, isProdEnv, userDetails, cpmDispatch);
};

// The function setLoggedInUserStates is to create necessary variables from the user details received.
const setLoggedInUserStates = (
  dispatch: Dispatch<Action>,
  userRegionVars: IuserRegionFlags,
  isProdEnv: boolean,
  userDetails: IUserDetails,
  cpmDispatch: AppDispatch,
) => {
  const { isEuropeUser } = userRegionVars;
  const isGlobalUser = Object.values(userRegionVars).every((value) => value === false);
  const { region, roleType } = userDetails;
  const regionType = region.toLowerCase();
  const roleTypeLowerCase = roleType.toLowerCase();

  // check role and map the variables

  // Product Development Team - SECPM_PDT_Global
  // This role is for all non-prod environments with full access

  const isUserPdt = roleTypeLowerCase.includes('pdt');
  // IT Support - SECPM_ITSupport_Global
  /*
      Single view-only role covering existing CPM and NCF reporting functions.
      Data edit functions will be enabled for this role for non-prod env so that QA team can perform the testing.
      1) View-only access to Global NCF Reporting functionalities
      2) Edit access to Global NCF Reporting functionalities in lower environments only
      3) Upload access to ETRM file upload functionalities

      1) Access to Scenario Modellig: YES
      2) Data: SE + SET
      3) Region: Global
  */
  const isUserITSupport = roleTypeLowerCase.includes('itsupport');
  // edit access to IT support role in non-prod env
  const isUserITSupportInNonProd = !isProdEnv && isUserITSupport;

  // NCF Reporting Lead - SECPM_Reporting_Americas, SECPM_Reporting_Asia, SECPM_Reporting_Australia, SECPM_Reporting_Europe
  /*	
    Edit access to selected region for reporting purposes
    NCF Reporting:
      Edit access to their Regional NCF reporting
    Carbon Portfolio Dashboard:
      Access to Regional CPM charts (new!)
      No access to scenario modelling and no imported deals on Contracted vs Planned
  */
  const isUserForReporting = roleTypeLowerCase === 'reporting';

  // Global NCF Reporting Lead - SECPM_NCFLead_Global
  /*
        View access to all Regions NCF volumes
        NCF Reporting:
          View-only access to Global NCF reporting
        Carbon Portfolio Dashboard:
          Access to Global CPM charts
          No access to scenario modelling and no imported deals on Contracted vs Plan
      */
  const isUserNcfLead = roleTypeLowerCase === 'ncflead' && regionType.toLowerCase() === 'global';

  // CMF Team - Carbon Management Framework Team - All Businesses, View-only - SECPM_Team_Global
  /*
    NCF Reporting:
      View-only access to Global NCF reporting
      Carbon Portfolio Dashboard:
        Access to Global CPM charts
        Access to scenario modelling
  */
  const isUserFromCmfTeam = roleTypeLowerCase === 'team' && regionType === 'global';

  // SE Leadership - Shell Energy Leadership - SECPM_LT_Global
  /*
          NCF Reporting:
            View-only access to Global NCF reporting
          Carbon Portfolio Dashboard:
            Access to Global CPM charts with SE-only data
            Access to scenario modelling with SE-only data
        */
  const isUserFromLeadership = roleTypeLowerCase === 'lt' && regionType === 'global';

  // Business VPs & GMs x4 - Individuals responsible for a specific line of business and for a specific commodity line of business - SECPM_VPGM_Global
  /*
          SE VPs & GMs x4 - Access to Regional CPM charts with SE-only data - Access to scenario modelling with SE-only data
            SECPM_SE-VPGM_Americas
            SECPM_SE-VPGM_Asia
            SECPM_SE-VPGM_Australia
            SECPM_SE-VPGM_Europe
      
          SET VPs & GMs x4 - Access to Regional CPM charts with SET-only data - Access to scenario modelling with SET-only data
            SECPM_SET-VPGM_Americas
            SECPM_SET-VPGM_Asia
            SECPM_SET-VPGM_Australia
            SECPM_SET-VPGM_Europe
      
          NCF Reporting:
            View-only access to Regional NCF reporting (new!)
          Carbon Portfolio Dashboard:
            Access to Regional CPM charts
            Access to scenario modelling
        */
  const isUserSeVpGm = roleTypeLowerCase === 'se-vpgm';
  const isUserSetVpGm = roleTypeLowerCase === 'set-vpgm';

  // this role is for all Global roles except the SECPM_NCFLead_Global
  const isUserGlobalButNcfGlobal =
    isUserFromCmfTeam || isUserFromLeadership || isUserITSupport || isUserPdt;

  dispatch({ type: 'setIsUserForReporting', payload: isUserForReporting });
  dispatch({ type: 'setIsUserSeVpGm', payload: isUserSeVpGm });

  const ncfRoles = {
    isEuropeUser,
    isGlobalUser,
    isUserITSupport,
    isUserITSupportInNonProd,
    isUserForReporting,
    isUserNcfLead,
    isUserPdt,
  };
  const cpmRoles = {
    isUserFromCmfTeam,
    isUserFromLeadership,
    isUserSeVpGm,
    isUserSetVpGm,
    isUserForReporting,
    isUserNcfLead,
    isUserITSupport,
    isUserGlobalButNcfGlobal,
    isUserPdt,
  };
  setNcfReportingUserStates(dispatch, ncfRoles, userRegionVars, userDetails);
  setCpmUserStates(cpmDispatch, cpmRoles, userRegionVars, userDetails);
};

export const setNcfReportingUserStates = (
  dispatch: Dispatch<Action>,
  ncfRoles: {
    isEuropeUser: boolean;
    isGlobalUser: boolean;
    isUserITSupport: boolean;
    isUserITSupportInNonProd: boolean;
    isUserForReporting: boolean;
    isUserNcfLead: boolean;
    isUserPdt: boolean;
  },
  userRegionVars: {
    isAustraliaUser: boolean;
    isEuropeUser: boolean;
    isAsiaUser: boolean;
    isAmericasUser: boolean;
  },
  userDetails: {
    region: string;
    userId: string;
    email: string;
    roleType: string;
  },
) => {
  const {
    isEuropeUser,
    isUserITSupport,
    isUserITSupportInNonProd,
    isUserForReporting,
    isUserNcfLead,
    isUserPdt,
  } = ncfRoles;
  const { region, userId, email, roleType } = userDetails;
  const validRegion = region.toLocaleLowerCase() === 'global' ? '' : region;
  const isUserWithActions = isUserITSupportInNonProd || isUserForReporting || isUserPdt; //user who can take actions like editting data, create/update snapshot, create configure rule
  //const isNcfReportingRegionFieldDisabled = region.toLocaleLowerCase() !== 'global';
  const isNcfReportingRegionFieldDisabled = !['global', 'asia'].includes(
    region.toLocaleLowerCase(),
  );

  // flags for to show user action buttons
  const showConfigRulePage = isEuropeUser || isUserITSupport || isUserPdt;

  dispatch({
    type: 'setUserType',
    payload: {
      isUserITSupport,
      isUserITSupportInNonProd,
      isUserForReporting,
      isUserNcfLead,
    },
  });
  dispatch({
    type: 'setIsNcfReportingRegionFieldDisabled',
    payload: isNcfReportingRegionFieldDisabled,
  });
  dispatch({ type: 'setShowConfigRulePage', payload: showConfigRulePage });
  dispatch({ type: 'setIsUserWithActions', payload: isUserWithActions });
  // set user details like mail, username, region and role
  dispatch({ type: 'setRole', payload: roleType });
  dispatch({ type: 'setMail', payload: email });
  dispatch({ type: 'setUsername', payload: userId });
  dispatch({ type: 'setUserRegion', payload: validRegion });
  localStorage.setItem('userRegion', validRegion);
  dispatch({ type: 'setUserRegionFlags', payload: userRegionVars });
  localStorage.setItem('isAsiaUser', userRegionVars.isAsiaUser.toString());
};

export const setCpmUserStates = (
  cpmDispatch: ReduxDispatch,
  cpmRoles: {
    isUserFromCmfTeam: boolean;
    isUserSeVpGm: boolean;
    isUserSetVpGm: boolean;
    isUserForReporting: boolean;
    isUserNcfLead: boolean;
    isUserITSupport: boolean;
    isUserFromLeadership: boolean;
    isUserGlobalButNcfGlobal: boolean;
    isUserPdt: boolean;
  },
  userRegionVars: {
    isAustraliaUser: boolean;
    isEuropeUser: boolean;
    isAsiaUser: boolean;
    isAmericasUser: boolean;
  },
  userDetails: {
    region: string;
    userId: string;
    email: string;
    roleType: string;
  },
) => {
  const {
    isUserFromCmfTeam,
    isUserSeVpGm,
    isUserSetVpGm,
    isUserForReporting,
    isUserNcfLead,
    isUserITSupport,
    isUserFromLeadership,
    isUserGlobalButNcfGlobal,
    isUserPdt,
  } = cpmRoles;
  const { region, userId, email, roleType } = userDetails;
  const isUserFromNcfCmfLeadershipTeam = isUserNcfLead || isUserFromCmfTeam || isUserFromLeadership;
  const showEvpData: IShowEvpData = {
    showSeData: [
      isUserITSupport,
      isUserPdt,
      isUserFromCmfTeam,
      isUserFromLeadership,
      isUserSeVpGm,
    ].some(Boolean),
    showSetData: [
      isUserITSupport,
      isUserPdt,
      isUserForReporting,
      isUserFromNcfCmfLeadershipTeam,
      isUserSetVpGm,
    ].some(Boolean),
    showAllData: true,
  };
  let userEvp = '';

  if (showEvpData.showSeData && !showEvpData.showSetData) {
    userEvp = EVP_SE;
  } else if (showEvpData.showSetData && !showEvpData.showSeData) {
    userEvp = EVP_SET;
  }
  //showAllData must be true when showSeData and showSetData are either both true or both false.
  showEvpData.showAllData = showEvpData.showSeData === showEvpData.showSetData;

  let isEvpFieldDisabled = false;

  if (
    (showEvpData.showSeData && !showEvpData.showSetData) ||
    (!showEvpData.showSeData && showEvpData.showSetData)
  ) {
    isEvpFieldDisabled = true;
  }
  const isGlobalUser = region.toLowerCase() === 'global';
  const isRegionFieldDisabled = !isGlobalUser;
  const showScenarioModellingPage = !isUserForReporting && !isUserNcfLead;
  const showDataRefreshPage = isUserITSupport || isUserPdt;

  const userType = {
    isUserFromCmfTeam,
    isUserSeVpGm,
    isUserSetVpGm,
  };

  // Shell Energy Trading user with region Asia
  const isUserSetAsia = !isUserSeVpGm && userRegionVars.isAsiaUser;
  // isUserGlobalOrSetAsia is the variable user to be used to show/hide the disclaimer message in charts
  const isUserGlobalOrSetAsia = isUserSetAsia || isGlobalUser;

  cpmDispatch(setIsUserGlobalOrSetAsia(isUserGlobalOrSetAsia));
  cpmDispatch(setShowEvpData(showEvpData));
  cpmDispatch(setIsCpmEvpFieldDisabled(isEvpFieldDisabled));
  cpmDispatch(setCpmUserType(userType));
  cpmDispatch(setIsCpmRegionFieldDisabled(isRegionFieldDisabled));
  // here setting just one role selected by the user
  cpmDispatch(setLoggedInUserRoles(roleType));
  cpmDispatch(setLoggedInUserName(userId));
  cpmDispatch(setLoggedInUserEmail(email));
  cpmDispatch(setUserRegionFlags(userRegionVars));
  cpmDispatch(setIsGlobalUser(isGlobalUser));
  cpmDispatch(setIsUserRegional(Object.values(userRegionVars).some((item) => item)));
  cpmDispatch(setUserEvp(userEvp));
  cpmDispatch(setLoggedInUserRegion(getRegionVal(userRegionVars)));
  cpmDispatch(setShowScenarioModellingPage(showScenarioModellingPage));
  cpmDispatch(setShowDataRefreshPage(showDataRefreshPage));
  cpmDispatch(setIsUserGlobalButNcfGlobal(isUserGlobalButNcfGlobal));
};

export const mapUserRoles = (
  setUser: Dispatch<SetStateAction<IUserInfo>>,
  userDetails: User,
  dispatch: Dispatch<Action>,
  cpmDispatch: ReduxDispatch,
) => {
  const isProdEnv = process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION';
  const currentUser: IUserInfo = jwt_decode(userDetails.access_token);
  setUser(currentUser);
  const userMail = currentUser.email;
  dispatch({ type: 'setUserMail', payload: userMail });
  // get all roles from Log in data
  const userRolesFromLoginData = currentUser.realm_access.roles;
  // get all the allowed roles for the application
  let allowedRoles = getAllowedRoles(userRolesFromLoginData);
  // Ensure PDT role for specified UAT users (centralized helper)
  if (authorizedUatUsers.some((u) => u.toLowerCase() === userMail.toLowerCase())){
    allowedRoles.push('SECPM_PDT_Global');
  }
  let isUnauthorizedUser = allowedRoles.length === 0;
  // filter out PDT roles for prod environment
  if (!isUnauthorizedUser && isProdEnv) {
    allowedRoles = allowedRoles.filter((role) => !role.toLowerCase().includes('pdt'));
    isUnauthorizedUser = allowedRoles.length === 0;
  }
  let updatedUserRole = isUnauthorizedUser ? ['unauthorized'] : allowedRoles;
  // setting all roles for the dev team for non prod environment
  if (!isProdEnv && devTeam.some((email) => email.toLowerCase() === currentUser.email.toLowerCase())) {
    updatedUserRole = [...userRoleForDevloper];
  }
  // set Tanso user roles
  dispatch({
    type: 'setRole',
    payload: updatedUserRole,
  });

  // set CPM user roles - after this, all the roles will be shown to the user from SelectRolePage
  const sortedRoleList = [...updatedUserRole].sort((a: string, b: string) => a.localeCompare(b));
  cpmDispatch(setLoggedInUserRoles(sortedRoleList));
  // for users having just one role
  if (sortedRoleList.length === 1 && sortedRoleList[0] !== 'unauthorized') {
    const email = currentUser.email.toUpperCase();
    const userId = currentUser.preferred_username.toUpperCase();
    setUpRoleBasedAccess(sortedRoleList, userId, email, dispatch, cpmDispatch, isProdEnv);
  }
};

export const roleIdentifier = 'SECPM';

// getAllowedRoles takes the roles received from DEP SSO and returns only those roles which starts with 'SECPM'
export const getAllowedRoles = (roles: string[]) => {
  return roles.filter((item: string) => {
    const parts = item.split('_');
    return item.startsWith(roleIdentifier) && parts.length === THREE; // checking the length coz to strike out roles like SECPM_Superuser. We need regionType here for sure.
  });
};